import { create } from 'zustand';
import { BusinessDto, BusinessListItemDto, BusinessSummary, CreateBusinessCommand, UpdateBusinessCommand } from '../../domain/entities/Business';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';
import { BusinessService } from '../services/BusinessService';
import { BusinessRepository } from '../../infrastructure/repositories/BusinessRepository';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';

/**
 * Business store state interface
 */
interface BusinessStoreState {
  // State
  businesses: BusinessListItemDto[]; // List items for DataTable display
  searchResults: BusinessSummary[];
  searchResultsFull: BusinessDto[]; // Full business data for search results
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchBusinesses: (params?: PaginationParams, userRole?: UserRole) => Promise<void>;
  searchBusinesses: (query: string, limit?: number) => Promise<void>;
  searchBusinessesFull: (query: string, limit?: number) => Promise<void>; // Returns full BusinessDto
  getBusinessesByName: (name: string) => Promise<BusinessDto[]>; // Get all businesses with exact name
  createBusiness: (command: CreateBusinessCommand, userRole?: UserRole) => Promise<void>;
  updateBusiness: (id: string, command: UpdateBusinessCommand, userRole: UserRole, userBusinessId: string | null) => Promise<void>;
  deactivateBusiness: (id: string, userRole: UserRole) => Promise<void>;
  clearSearchResults: () => void;
  clearError: () => void;
}

/**
 * Create service dependencies
 */
const businessRepository = new BusinessRepository(apiClient);
const businessService = new BusinessService(businessRepository);

/**
 * Business Store - Manages business state
 *
 * Provides:
 * - Businesses list with pagination
 * - Search functionality for registration
 * - CRUD operations with role-based access
 */
export const useBusinessStore = create<BusinessStoreState>((set, get) => ({
  // Initial state
  businesses: [],
  searchResults: [],
  searchResultsFull: [],
  isLoading: false,
  error: null,

  /**
   * Fetch all businesses (admin only)
   * Returns BusinessListItemDto for DataTable display with statistics
   */
  fetchBusinesses: async (params?: PaginationParams, userRole?: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      const result: PaginatedResult<BusinessListItemDto> = await businessService.getAllBusinesses(params, userRole);

      set({
        businesses: result.items,
        isLoading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch businesses';

      set({
        businesses: [],
        isLoading: false,
        error: errorMessage
      });
    }
  },

  /**
   * Search businesses by name (public - for registration)
   * Returns BusinessSummary (minimal data)
   */
  searchBusinesses: async (query: string, limit: number = 10): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      const results = await businessService.searchBusinesses(query, limit);

      set({
        searchResults: results,
        isLoading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to search businesses';

      set({
        searchResults: [],
        isLoading: false,
        error: errorMessage
      });
    }
  },

  /**
   * Search businesses and return full BusinessDto objects
   * Used in predictive search for registration
   */
  searchBusinessesFull: async (query: string, limit: number = 10): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      // First search to get IDs
      const summaries = await businessService.searchBusinesses(query, limit);

      // Fetch full details for each result
      const fullBusinesses: BusinessDto[] = [];
      for (const summary of summaries) {
        try {
          // We need a method to get BusinessDto by ID
          // For now, we'll use the businesses from fetchBusinesses if available
          // This is a limitation - ideally backend should return full data
          const existing = get().businesses.find((b) => b.id === summary.id);
          if (existing) {
            // Convert BusinessListItemDto to BusinessDto
            fullBusinesses.push({
              id: existing.id,
              name: existing.name,
              category: existing.category ?? 'Unknown',
              isActive: existing.isActive,
              businessType: null,
              description: existing.description,
              phone: null,
              website: existing.website,
              email: null,
              openingHours: null,
              regionId: existing.regionId,
              addressLine1: null,
              addressLine2: null,
              isUserCreated: false
            });
          } else {
            // Fallback: create a partial BusinessDto from summary
            fullBusinesses.push({
              id: summary.id,
              name: summary.name,
              category: summary.category ?? 'Unknown',
              isActive: summary.isActive,
              businessType: null,
              description: null,
              phone: null,
              website: summary.website,
              email: null,
              openingHours: null,
              regionId: summary.regionId,
              addressLine1: summary.addressLine1,
              addressLine2: summary.addressLine2,
              isUserCreated: false
            });
          }
        } catch (err) {
          // Skip businesses that fail to load
          console.error(`Failed to load business ${summary.id}:`, err);
        }
      }

      set({
        searchResultsFull: fullBusinesses,
        searchResults: summaries,
        isLoading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to search businesses';

      set({
        searchResults: [],
        searchResultsFull: [],
        isLoading: false,
        error: errorMessage
      });
    }
  },

  /**
   * Get all businesses matching an exact name
   * Used to detect multi-region or multi-location scenarios
   */
  getBusinessesByName: async (name: string): Promise<BusinessDto[]> => {
    set({ isLoading: true, error: null });

    try {
      // Search for businesses with this name
      const summaries = await businessService.searchBusinesses(name, 50);

      // Filter for exact matches (case-insensitive)
      const exactMatches = summaries.filter(
        (b) => b.name.toLowerCase() === name.toLowerCase()
      );

      // Fetch full details for each match
      // For now, return partial data - this would ideally call a backend endpoint
      const fullBusinesses: BusinessDto[] = exactMatches.map((summary) => ({
        id: summary.id,
        name: summary.name,
        category: summary.category ?? 'Unknown',
        isActive: summary.isActive,
        businessType: null,
        description: null,
        phone: null,
        website: null,
        email: null,
        openingHours: null,
        regionId: '', // Would need backend support
        addressLine1: null,
        addressLine2: null,
        isUserCreated: false
      }));

      set({
        isLoading: false,
        error: null
      });

      return fullBusinesses;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get businesses by name';

      set({
        isLoading: false,
        error: errorMessage
      });

      return [];
    }
  },

  /**
   * Create a new business
   */
  createBusiness: async (command: CreateBusinessCommand, userRole?: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await businessService.createBusiness(command, userRole);

      // Refetch businesses if we have them loaded
      if (get().businesses.length > 0 && userRole === 'Admin') {
        await get().fetchBusinesses(undefined, userRole);
      }

      set({
        isLoading: false,
        error: null
      });

      // Show success notification
      notificationService.showSuccess(`Business "${command.name}" has been created successfully`, 'Business Created');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create business';

      set({
        isLoading: false,
        error: errorMessage
      });

      // Error notification already shown by ApiClient
      throw error;
    }
  },

  /**
   * Update an existing business
   */
  updateBusiness: async (
    id: string,
    command: UpdateBusinessCommand,
    userRole: UserRole,
    userBusinessId: string | null
  ): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await businessService.updateBusiness(id, command, userRole, userBusinessId);

      // Update only the fields that exist in both BusinessListItemDto and UpdateBusinessCommand
      const businesses = get().businesses.map((business) =>
        business.id === id
          ? {
              ...business,
              name: command.name ?? business.name,
              description: command.description ?? business.description,
              website: command.website ?? business.website,
              category: command.category ?? business.category
            }
          : business
      );

      set({
        businesses,
        isLoading: false,
        error: null
      });

      // Show success notification
      notificationService.showSuccess('Business has been updated successfully', 'Business Updated');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update business';

      set({
        isLoading: false,
        error: errorMessage
      });

      // Error notification already shown by ApiClient
      throw error;
    }
  },

  /**
   * Deactivate a business (admin only)
   * Sprint 045: Renamed from deleteBusiness
   */
  deactivateBusiness: async (id: string, userRole: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await businessService.deactivateBusiness(id, userRole);

      // Update the business in local state (mark as inactive, don't remove)
      const businesses = get().businesses.map((business) =>
        business.id === id ? { ...business, isActive: false } : business
      );

      set({
        businesses,
        isLoading: false,
        error: null
      });

      notificationService.showSuccess('Business has been deactivated', 'Business Deactivated');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to deactivate business';

      set({
        isLoading: false,
        error: errorMessage
      });

      throw error;
    }
  },

  /**
   * Clear search results
   */
  clearSearchResults: (): void => {
    set({ searchResults: [], searchResultsFull: [] });
  },

  /**
   * Clear error state
   */
  clearError: (): void => {
    set({ error: null });
  }
}));

/**
 * Selectors - Memoized state selectors for better performance
 */

/**
 * Get business by ID from current state (list item)
 */
export const useBusinessById = (id: string): BusinessListItemDto | undefined => {
  return useBusinessStore((state) => state.businesses.find((business) => business.id === id));
};

/**
 * Get search results
 */
export const useSearchResults = (): BusinessSummary[] => {
  return useBusinessStore((state) => state.searchResults);
};

/**
 * Get all businesses (list items for DataTable)
 */
export const useBusinesses = (): BusinessListItemDto[] => {
  return useBusinessStore((state) => state.businesses);
};

/**
 * Get loading state
 */
export const useBusinessesLoading = (): boolean => {
  return useBusinessStore((state) => state.isLoading);
};

/**
 * Get error state
 */
export const useBusinessesError = (): string | null => {
  return useBusinessStore((state) => state.error);
};
